import IdeaProperty from "@/types/IdeaProperty";
import Idea from "@/types/Idea";
import {deepCopy} from "@/utils/copy"
import {Component, Vue, Prop} from "vue-facing-decorator";
import {EnhancedWorkpace} from "@/types/Workspace";
import {namingError} from "@/utils/fieldValidation";
import {fillable, PropertyRule} from "@/types/IdeaProperty";
import {isMedia} from "@/utils/mediaTypes";
import {isChoiceField} from "@/utils/propertyDisplay";
import AsyncFormulaInput from "@/components/design/property/asyncFormulaInput";
import {editorsValid} from "@/components/design/property/editors";
import RulesInput from "@/components/design/property/RulesInput.vue";
import OptionFilterInput from "@/components/design/property/OptionFilterInput.vue";
import AccessInput from "@/components/design/class/AccessInput.vue";
import {PROPERTY_ACCESS_KINDS, PropertyAccess} from "@/types/Idea";
import PropertyTabs, {PROPERTY_TABS, PropertyTab} from "@/components/design/property/PropertyTabs.vue";

/** O que as regras e as opções permitidas olham no modelo de um editor — de qualquer lado da associação. */
type WrittenModel = {
    computed?: boolean, computedWhen?: string, type?: string, rules?: PropertyRule[], optionFilter?: string,
    optionFilterMessage?: string
}

/**
 * Pode ter regra? Não a calculada de sempre — a mensagem cairia num campo que ninguém edita (decisão do
 * usuário) —, nem a mídia, que é um arquivo enviado. O "Calculado quando" pode (`fillable`): a regra julga o
 * preenchido. O espelho pode (pedido do usuário): o `self` dele é a lista de quem aponta para cá.
 * Um dono para o que a tela oferece e o que vai gravado (`rulesValue`).
 *
 * Funções do modelo, e não só getters do editor: o da associação as aplica aos dois lados.
 */
export function canHaveRules(model: WrittenModel): boolean {
    return fillable(model) && !isMedia(model.type)
}

/** As regras como vão gravadas: nenhuma onde não cabem, e lista vazia é não ter regra. */
export function rulesValue(model: WrittenModel): PropertyRule[] | undefined {
    return canHaveRules(model) && model.rules && model.rules.length ? model.rules : undefined
}

/**
 * Pode ter "Opções permitidas"? Campo de escolha que alguém preenche: o relacionamento — direto ou
 * espelho, os modelos sem `type` —, a seleção e o usuário (`isChoiceField`); não a calculada de sempre,
 * em que a fórmula escolhe por ela (`fillable`). Um dono para o que a tela oferece e o que vai gravado
 * (`optionFilterFields`), como nas regras.
 */
export function canFilterOptions(model: WrittenModel): boolean {
    return fillable(model) && (model.type === undefined || isChoiceField(model as IdeaProperty))
}

/**
 * O filtro e a mensagem dele como vão gravados: nenhum onde não cabe, o texto vazio é não ter
 * filtro, e sem filtro não há mensagem. Os formulários os espalham no `currentValue`.
 */
export function optionFilterFields(model: WrittenModel): Pick<IdeaProperty, 'optionFilter' | 'optionFilterMessage'> {
    const filter = canFilterOptions(model) && model.optionFilter ? model.optionFilter : undefined
    return {optionFilter: filter, optionFilterMessage: (filter && model.optionFilterMessage) || undefined}
}

/** Quem vê e quem edita o campo, como vai gravado: sem pergunta nenhuma é não ter acesso. */
export function accessValue(access: PropertyAccess | undefined): PropertyAccess | undefined {
    return access && Object.keys(access).length ? access : undefined
}

//Classe-base dos formulários de propriedade, e não componente: sem toNative.
//As abas, o editor de fórmula, o de regras, o de opções permitidas e o de acesso são registrados aqui, para todos.
@Component({emits: ['input'], components: {
    'ontos-property-tabs': PropertyTabs,
    'ontos-formula-input': AsyncFormulaInput,
    'ontos-rules-input': RulesInput,
    'ontos-option-filter-input': OptionFilterInput,
    'ontos-access-input': AccessInput
}})
export default class PropertyInput extends Vue {
    @Prop({type: Object, required: true}) readonly value!: IdeaProperty
    @Prop({type: Boolean, default: false}) readonly fixedAlias!: boolean
    @Prop({type: Object, required: true}) readonly idea!: Idea
    /**
     * O ambiente: os domínios do atributo de seleção, e — nas fórmulas e nas regras — o ambiente que
     * o editor consulta e as cores das opções e situações que a fórmula cita.
     */
    @Prop({type: Object, default: undefined}) readonly workspace!: EnhancedWorkpace
    /**
     * O que o editor guardava por dentro na janela, ao expandir para a página (`snapshot`), ou null — cada
     * editor sabe o que é o seu: as opções do domínio mexidas, o nome e o identificador digitados à mão.
     */
    @Prop({default: null}) readonly resumed!: any

    manualAlias = false
    manualName = false
    model = {
        name: "",
        alias: "",
    }
    errorMessages = {
        name: "",
        alias: ""
    }

    emit(changedProperties: Partial<IdeaProperty>) {
        this.$emit('input', deepCopy(this.value, changedProperties))
    }

    /** Confere o nome ou o alias entre as outras propriedades da coleção — ver `namingError`. */
    checkNaming(prop: 'name' | 'alias', text: string) {
        this.errorMessages[prop] = namingError(text, prop, this.idea.properties, this.fixedAlias ? this.value.alias : undefined)
        return !this.errorMessages[prop]
    }

    validateAlias(text: string = this.model.alias) {
        return this.checkNaming('alias', text)
    }

    validateName(text: string = this.model.name) {
        return this.checkNaming('name', text)
    }

    /**
     * Confere as três abas (`PropertyTabs`) — todas, e não até a primeira que falha: cada campo acusa o
     * seu de uma vez, e não um por salvar. A aba com o que não confere ganha o ponto vermelho, e a
     * primeira delas se abre se a aberta estiver em ordem (decisão do usuário).
     */
    validateModel(): boolean {
        this.invalidTabs = PROPERTY_TABS.filter(tab => !this.validTab(tab))
        const tabs = this.$refs.tabs as { reveal(invalid: PropertyTab[]): void } | undefined
        if (tabs) tabs.reveal(this.invalidTabs)
        return !this.invalidTabs.length
    }

    /** As abas que não conferiram no último salvar — o ponto vermelho de cada uma. */
    invalidTabs: PropertyTab[] = []

    /**
     * Uma aba confere? Validação: as regras e as opções permitidas; Acessos: "Visível quando" e "Editável
     * quando" — em lista quando o ref está num v-for, os dois lados da associação. Regra, filtro ou acesso
     * que não confere não salva, e o servidor recusaria do mesmo jeito.
     */
    validTab(tab: PropertyTab): boolean {
        if (tab === 'property') return this.validProperty()
        return (tab === 'validation' ? ['rules', 'optionFilter'] : ['access'])
            .map(ref => editorsValid(this.$refs[ref]))
            .every(Boolean)
    }

    /**
     * A aba Propriedade: o nome, o alias e os editores dela — as fórmulas do calculado, o Comportamento (a
     * fórmula do "Obrigatório quando") e o padrão. Cada um só está na tela quando vale. Um gancho, e não
     * `super` no filho — os componentes de classe juntam os métodos num objeto de opções, e o `super` não é
     * garantido ali: o relacionamento põe o seu, com os dois lados.
     */
    validProperty(): boolean {
        return [this.validateAlias(), this.validateName(), this.validEditors()].every(Boolean)
    }

    /** Os editores da aba Propriedade conferem — ver `validProperty`; o `computedWhen`, a condição do cálculo. */
    validEditors(): boolean {
        return ['formula', 'computedWhen', 'behavior', 'default'].map(ref => editorsValid(this.$refs[ref])).every(Boolean)
    }

    //O modelo de cada formulário estende o da base, que só declara o nome e o alias
    get canHaveRules(): boolean {
        return canHaveRules(this.model as WrittenModel)
    }

    get rulesValue(): PropertyRule[] | undefined {
        return rulesValue(this.model as WrittenModel)
    }

    get canFilterOptions(): boolean {
        return canFilterOptions(this.model as WrittenModel)
    }

    get optionFilterFields(): Pick<IdeaProperty, 'optionFilter' | 'optionFilterMessage'> {
        return optionFilterFields(this.model as WrittenModel)
    }

    /**
     * O que o editor de fórmula recebe igual no atributo e no relacionamento calculados. O rascunho
     * (`draft`) e o tipo do resultado (`result-label`) são de cada formulário, e vão à parte.
     */
    get formulaBindings() {
        return {
            label: this.$tt('formula'), systemAlias: this.workspace.alias, idea: this.idea,
            choiceDomains: this.workspace.domains,
            current: this.fixedAlias ? this.value.alias : undefined
        }
    }

    /**
     * O que o editor de acesso recebe para QUEM VÊ e QUEM EDITA um campo (pedido do usuário) — igual no
     * atributo e em cada lado do relacionamento, que mudam só o rascunho, a coleção e o alias. O campo do
     * identificador não oferece "Quem vê" (decisão do usuário): o nome do item o mostraria.
     */
    fieldAccessBindings(draft: Partial<IdeaProperty>, idea: Idea = this.idea, current?: string) {
        return {
            kinds: PROPERTY_ACCESS_KINDS, draft, current, idea, workspace: this.workspace,
            explain: this.$tt('access.field-explain'),
            blocked: draft.identifier ? {visible: this.$tt('access.identifier-blocked')} : {}
        }
    }

    /**
     * Grava o que é de OUTRA entidade e precisa ir ANTES da propriedade. Devolve false para o
     * host desistir de salvar.
     *
     * Aqui não há nada a gravar; quem tem é o atributo de seleção, que edita as opções do
     * domínio escolhido no próprio formulário. O domínio é entidade do ambiente, com rota
     * própria — não entra no patch da propriedade, e tem de estar gravado antes dela, senão a
     * propriedade apontaria para uma opção que o servidor ainda não conhece.
     */
    async commit(): Promise<boolean> {
        return true
    }

    /** O que levar ao expandir, além do rascunho (ver o `resumed`). Nada, aqui. */
    snapshot(): unknown {
        return null
    }
}